export type DashboardTabValue =
  | 'providers'
  | 'models'
  | 'agents'
  | 'assistants'
  | 'automations'
  | 'workflows'
  | 'factories'
  | 'pages'
  | 'schedules'
  | 'embeddings'
  | 'studio'
  | 'chat'
  | 'mcp'

export interface DashboardTabRoute {
  value: DashboardTabValue
  childPath: string
  path: string
  routeName: string
  title: string
  feature: string
}

export const defaultDashboardTab: DashboardTabValue = 'agents'
export const defaultDashboardPath = '/dashboard/agents'

export type UserMode = 'simple' | 'advanced'

// Tabs shown to non-technical ("simple") users, in the order they appear. Advanced users see all tabs.
// Models + Agents are conglomerated into Assistants here, so they are NOT in simple mode.
export const SIMPLE_DASHBOARD_TABS: DashboardTabValue[] = [
  'assistants',
  'automations',
  'chat',
  'providers',
  'mcp'
]

// Tab order for advanced users. Must match the advanced v-window-item order in DashboardView.vue so
// slide animations move in the right direction. Excludes the retiring 'studio' direct route and the
// simple-only lenses; neither is surfaced as an advanced tab chip.
export const ADVANCED_DASHBOARD_TABS: DashboardTabValue[] = [
  'providers',
  'models',
  'agents',
  'workflows',
  'factories',
  'pages',
  'schedules',
  'embeddings',
  'chat',
  'mcp'
]

export function isTabVisibleForMode(value: DashboardTabValue, mode: UserMode | null): boolean {
  // Assistants and Automations are the simplified lenses — only simple mode surfaces them. Advanced
  // users work directly in the real Models / Agents / Workflows / Factories / Schedules editors.
  if (value === 'automations' || value === 'assistants') {
    return mode === 'simple'
  }
  if (mode === 'simple') {
    return SIMPLE_DASHBOARD_TABS.includes(value)
  }
  return true
}

export const dashboardTabs: DashboardTabRoute[] = [
  {
    value: 'providers',
    childPath: 'providers',
    path: '/dashboard/providers',
    routeName: 'Dashboard Providers',
    title: 'Providers - MissionSquad.ai',
    feature: 'providers'
  },
  {
    value: 'models',
    childPath: 'models',
    path: '/dashboard/models',
    routeName: 'Dashboard Models',
    title: 'Models - MissionSquad.ai',
    feature: 'models'
  },
  {
    value: 'agents',
    childPath: 'agents',
    path: defaultDashboardPath,
    routeName: 'Dashboard Agents',
    title: 'Agents - MissionSquad.ai',
    feature: 'agents'
  },
  {
    value: 'assistants',
    childPath: 'assistants',
    path: '/dashboard/assistants',
    routeName: 'Dashboard Assistants',
    title: 'Assistants - MissionSquad.ai',
    feature: 'assistants'
  },
  {
    value: 'automations',
    childPath: 'automations',
    path: '/dashboard/automations',
    routeName: 'Dashboard Automations',
    title: 'Automations - MissionSquad.ai',
    feature: 'automations'
  },
  {
    value: 'workflows',
    childPath: 'workflows',
    path: '/dashboard/workflows',
    routeName: 'Workflows',
    title: 'Workflows - MissionSquad.ai',
    feature: 'workflows'
  },
  {
    value: 'factories',
    childPath: 'factories',
    path: '/dashboard/factories',
    routeName: 'Factories',
    title: 'Factories - MissionSquad.ai',
    feature: 'factories'
  },
  {
    value: 'pages',
    childPath: 'pages',
    path: '/dashboard/pages',
    routeName: 'Dashboard Pages',
    title: 'Pages - MissionSquad.ai',
    feature: 'pages'
  },
  {
    value: 'schedules',
    childPath: 'schedules',
    path: '/dashboard/schedules',
    routeName: 'Dashboard Schedules',
    title: 'Schedules - MissionSquad.ai',
    feature: 'schedules'
  },
  {
    value: 'embeddings',
    childPath: 'embeddings',
    path: '/dashboard/embeddings',
    routeName: 'Dashboard Embeddings',
    title: 'Embeddings - MissionSquad.ai',
    feature: 'embeddings'
  },
  {
    value: 'studio',
    childPath: 'studio',
    path: '/dashboard/studio',
    routeName: 'Dashboard Prompt Studio',
    title: 'Prompt Studio - MissionSquad.ai',
    feature: 'studio'
  },
  {
    value: 'chat',
    childPath: 'chat',
    path: '/dashboard/chat',
    routeName: 'Dashboard Chat',
    title: 'Chat - MissionSquad.ai',
    feature: 'chat'
  },
  {
    value: 'mcp',
    childPath: 'mcp',
    path: '/dashboard/mcp',
    routeName: 'Dashboard MCP',
    title: 'MCP - MissionSquad.ai',
    feature: 'mcp'
  }
]

export const dashboardTabPathByValue: Record<DashboardTabValue, string> = {
  providers: '/dashboard/providers',
  models: '/dashboard/models',
  agents: defaultDashboardPath,
  assistants: '/dashboard/assistants',
  automations: '/dashboard/automations',
  workflows: '/dashboard/workflows',
  factories: '/dashboard/factories',
  pages: '/dashboard/pages',
  schedules: '/dashboard/schedules',
  embeddings: '/dashboard/embeddings',
  studio: '/dashboard/studio',
  chat: '/dashboard/chat',
  mcp: '/dashboard/mcp'
}

const dashboardTabValues = new Set<string>(dashboardTabs.map((tab) => tab.value))

export function isDashboardTabValue(value: string): value is DashboardTabValue {
  return dashboardTabValues.has(value)
}

export function toDashboardTabValue(value: unknown): DashboardTabValue | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  return isDashboardTabValue(value) ? value : undefined
}

export function getDashboardTabPath(tab: DashboardTabValue): string {
  return dashboardTabPathByValue[tab]
}

export function getDashboardTabFromPath(path: string): DashboardTabValue | undefined {
  return dashboardTabs.find((tab) => path === tab.path || path.startsWith(`${tab.path}/`))?.value
}
